/**
 * Purchase Invoice mapping — Access `frmReportCashFlowQryMuaHang_add`.
 *
 * Pick an ERP purchase invoice + a MISA invoice; Save writes the link into
 * `report_cashflow_map_sohoadon_muahang` (New if no map id, else Update).
 * Layout mirrors the Access `_add` single form 1:1 (same 2-col pattern as
 * CpMappingEditDialog): LEFT = ERP group (invoice picker + readonly snapshot),
 * RIGHT = MISA group (invoice picker + readonly snapshot). Snapshot fills from
 * the picked option; the two combos are the only editable controls.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowDetailLookupQuery,
  useSaveCashflowMapMuaHangMutation,
  type MapMuaHang,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { compactDialogContentSx, formatStandard, numericInputProps, readonlyFieldSx } from './formHelpers'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  row: MapMuaHang | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function MuaHangMapDialog({ open, mode, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [erpInput, setErpInput] = useState('')
  const [misaInput, setMisaInput] = useState('')
  const [erp, setErp] = useState<Row | null>(null)
  const [misa, setMisa] = useState<Row | null>(null)
  const [save, { isLoading }] = useSaveCashflowMapMuaHangMutation()

  const { data: erpData } = useGetCashflowDetailLookupQuery({ kind: 'erp_muahang', q: erpInput || undefined }, { skip: !open })
  const { data: misaData } = useGetCashflowDetailLookupQuery({ kind: 'misa_muahang', q: misaInput || undefined }, { skip: !open })

  const erpLabel = (r: Row) => `${s(r.erp_cp)} - ${s(r.erp_misa_code)} - ${s(r.erp_inv_no)}`
  const misaLabel = (r: Row) => `${s(r.nhacungcap)} - ${s(r.sohoadon)} - ${s(r.sochungtu)}`

  const erpOptions = useMemo(() => erpData?.items ?? [], [erpData])
  const misaOptions = useMemo(() => misaData?.items ?? [], [misaData])
  // Inject the seeded (edit) selection so the chip shows + no MUI "no match" warning.
  const erpMerged = useMemo(
    () => (erp && !erpOptions.some((o) => erpLabel(o) === erpLabel(erp)) ? [erp, ...erpOptions] : erpOptions),
    [erp, erpOptions],
  )
  const misaMerged = useMemo(
    () => (misa && !misaOptions.some((o) => s(o.refid) === s(misa.refid)) ? [misa, ...misaOptions] : misaOptions),
    [misa, misaOptions],
  )

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      // Guard by presence so seeding from the Need viewer (ERP-only row) or the
      // Misa Invoices viewer (MISA-only row) leaves the other side empty.
      setErp(
        row.erpCp || row.erpInvNo
          ? {
              erp_cp: row.erpCp, erp_cp_name: (row as unknown as Row).erpCpName, erp_misa_code: row.erpMisaCode,
              erp_inv_no: row.erpInvNo, contract_no: row.contractNo, invoice_date: row.invoiceDate,
              invoice_amount: row.invoiceAmount, tab_name: row.erpTabName,
              paydata_requested: row.paydataRequested, paydata_paid: row.paydataPaid,
            }
          : null,
      )
      setMisa(
        row.refid
          ? {
              refid: row.refid, nhacungcap: row.nhacungcap, sohoadon: row.sohoadon, sochungtu: row.sochungtu,
              ngayhoadon: row.ngayhoadon, tab_name: row.tabName,
              thanhtien_quydoi: row.thanhtienQuydoi, thanhtoan_quydoi: row.thanhtoanQuydoi,
              journal_memo: (row as unknown as Row).journalMemo,
            }
          : null,
      )
    } else {
      setErp(null)
      setMisa(null)
    }
    setErpInput('')
    setMisaInput('')
  }, [open, mode, row])

  const handleSave = async () => {
    if (!canEdit) return
    if (!s(erp?.erp_inv_no) || !s(erp?.erp_cp)) {
      window.alert('Please choose ERP INVOICE without NULL in InvoiceNo, CP Code!')
      return
    }
    if (!row?.misaMapId && !s(misa?.refid)) {
      window.alert('Please choose MISA INVOICE in the INSERT query!')
      return
    }
    try {
      const res = await save({
        misaMapId: mode === 'edit' ? row?.misaMapId ?? null : null,
        erpCp: s(erp?.erp_cp),
        erpInvNo: s(erp?.erp_inv_no),
        erpInvDate: erp?.invoice_date ? s(erp.invoice_date) : null,
        erpMisaCode: s(erp?.erp_misa_code),
        misaCp: s(misa?.nhacungcap),
        misaInvNo: s(misa?.sohoadon),
        misaInvDate: misa?.ngayhoadon ? s(misa.ngayhoadon) : null,
        misaRef: s(misa?.refid),
        // Access: misa_object_code = rst!nhacungcap; validated == erp_misa_code.
        misaObjectCode: s(misa?.nhacungcap),
      }).unwrap()
      snack.success(res.message || 'Successfully Added')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  const ro = { size: 'small' as const, fullWidth: true, InputProps: { readOnly: true }, InputLabelProps: { shrink: true } }
  const num = { ...ro, inputProps: numericInputProps }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitleBar
        title="Purchase Invoice - Misa Invoice"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit}
      />
      <DialogContent sx={compactDialogContentSx}>
        {/* Access `_add` 2-col layout mirrored 1:1 (by Top):
            LEFT = ERP group (ERP invoice picker + readonly snapshot),
            RIGHT = MISA group (MISA invoice picker + readonly snapshot). */}
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          {/* Left column — ERP */}
          <Autocomplete
            size="small"
            options={erpMerged}
            getOptionLabel={erpLabel}
            isOptionEqualToValue={(o, v) => erpLabel(o) === erpLabel(v)}
            filterOptions={(x) => x}
            value={erp}
            onChange={(_, v) => setErp(v)}
            inputValue={erpInput}
            onInputChange={(_, v) => setErpInput(v)}
            renderOption={(props, o, { index }) => (
              <li {...props} key={`${erpLabel(o)}#${index}`}>{erpLabel(o)}</li>
            )}
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            renderInput={(p) => <TextField {...p} label="ERP Invoice" placeholder="Search CP, code or invoice no…" InputLabelProps={{ shrink: true }} />}
          />
          <TextField label="ERP - Misa Code" value={s(erp?.erp_misa_code)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 2 } }} />
          <TextField label="ERP - Invoice No" value={s(erp?.erp_inv_no)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 3 } }} />
          <TextField label="ERP - CP Code" value={s(erp?.erp_cp)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 4 } }} />
          <TextField label="ERP - Contract No" value={s(erp?.contract_no)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 5 } }} />
          <TextField label="ERP - CP Name" value={s(erp?.erp_cp_name)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 6 } }} />
          <TextField label="ERP - Tab Name" value={s(erp?.tab_name)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 7 } }} />
          <TextField label="ERP - Invoice Date" value={s(erp?.invoice_date)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 8 } }} />
          <TextField label="ERP - Invoice Amount" value={formatStandard(erp?.invoice_amount as number)} {...num} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 9 } }} />
          <TextField label="ERP - Payment Requested" value={formatStandard(erp?.paydata_requested as number)} {...num} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 10 } }} />
          <TextField label="ERP - Payment Paid" value={formatStandard(erp?.paydata_paid as number)} {...num} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 10 } }} />
          {/* Right column — MISA */}
          <Autocomplete
            size="small"
            options={misaMerged}
            getOptionLabel={misaLabel}
            isOptionEqualToValue={(o, v) => s(o.refid) === s(v.refid)}
            filterOptions={(x) => x}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            renderOption={(props, o, { index }) => (
              <li {...props} key={`${s(o.refid)}#${index}`}>
                {misaLabel(o)}
                {s(o.journal_memo) ? ` · ${s(o.journal_memo)}` : ''}
              </li>
            )}
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }}
            renderInput={(p) => <TextField {...p} label="MISA Invoice" placeholder="Search vender, invoice, voucher or description…" InputLabelProps={{ shrink: true }} />}
          />
          <TextField label="Misa - Refid" value={s(misa?.refid)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 } }} />
          <TextField label="Misa - Invoice No" value={s(misa?.sohoadon)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 3 } }} />
          <TextField label="Misa - Vender" value={s(misa?.nhacungcap)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 } }} />
          <TextField label="Misa - Tab Name" value={s(misa?.tab_name)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 } }} />
          <TextField label="Misa - Voucher No" value={s(misa?.sochungtu)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }} />
          <TextField label="Misa - Invoice Date" value={s(misa?.ngayhoadon)} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 7 } }} />
          <TextField label="Misa - Amount (Convert)" value={formatStandard(misa?.thanhtien_quydoi as number)} {...num} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 8 } }} />
          <TextField label="Misa - Payment (Convert)" value={formatStandard(misa?.thanhtoan_quydoi as number)} {...num} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 9 } }} />
          <TextField label="Misa - Description" value={s(misa?.journal_memo)} multiline minRows={3} {...ro} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: '1 / -1' }, gridRow: { sm: 11 } }} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
